import { Box, Stack, Typography, useTheme } from '@mui/material'
import { FC, useState } from 'react'
import { Dialogs } from '../../sharedComponents/src/Contexts/DialogsContext'
import BaseDialog from '../../sharedComponents/src/Dialogs/Common/BaseDialog'
import { updateFTUE } from '../../sharedComponents/src/globalHeader/functions/ams-functions'
import { useScreenSize } from '../../sharedComponents/src/hooks/useScreenSize'
import { useStoreActions, useStoreState } from '../../store/hooks'
import { FTUEContent } from './content'

interface FTUEDialogProps {
    activeContent: FTUEContent
}

export const FTUEDialog: FC<FTUEDialogProps> = ({ activeContent }) => {
    const userData = useStoreState((state) => state.userData)
    const setUserData = useStoreActions((state) => state.setUserData)
    const setManualActiveFTUE = useStoreActions((state) => state.setManualActiveFTUE)
    const [currentStep, setCurrentStep] = useState<number>(0)
    const theme = useTheme()
    const { isMobile } = useScreenSize()

    const isLastStep = currentStep === activeContent?.content.length - 1
    const hasMultipleSteps = activeContent?.content.length > 1

    const handleClose = () => {
        setManualActiveFTUE('')
        setCurrentStep(0)
        updateFTUE({ [activeContent.key]: true }).then(() => {
            if (userData.userData)
                setUserData({
                    ...userData,
                    userData: { ...userData.userData, ftue: { ...userData.userData.ftue, [activeContent.key]: true } },
                })
        })
    }

    const handleNext = () => {
        if (isLastStep) {
            handleClose()
        } else {
            setCurrentStep((prev) => prev + 1)
        }
    }

    const handlePrev = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0))
    }

    return (
        <BaseDialog
            fullScreen={false}
            borderColor={theme.palette.primary.main}
            name={Dialogs.FTUE}
            onClose={handleClose}
            gap="40px"
            // paddingLess
            sx={{
                p: '!important',
                '.MuiDialogContent-root': {
                    flexGrow: 1,
                    gap: { xs: '28px', md: '40px' },
                    p: { xs: '60px 18px 60px 18px', md: '30px 80px 60px 80px' },
                    justifyContent: 'center',
                },
                '.MuiDialog-paper': {
                    padding: '0px',
                    width: '100%',
                    maxWidth: { xs: '90%', md: '600px' },
                    height: 'unset',
                    maxHeight: 'unset',
                    minHeight: 'unset',
                    aspectRatio: { xs: 'unset', md: '1/1' },
                },
                img: {
                    height: { xs: '80px', md: '150px' },
                    px: '18px',
                },
                '#close-button': {
                    top: '8px',
                    right: '8px',
                },
            }}
            stepper={
                hasMultipleSteps
                    ? {
                          steps: activeContent.content.length,
                          activeStep: currentStep,
                          onBack: handlePrev,
                          onNext: handleNext,
                      }
                    : undefined
            }
        >
            {/* Title */}
            <Stack sx={{ width: '100%', textAlign: 'center' }}>
                <Typography
                    variant={isMobile ? 'h5' : 'h3'}
                    sx={{
                        textTransform: 'uppercase',
                        color: 'white',
                        lineHeight: 1,
                    }}
                >
                    {activeContent.content[currentStep].prefix || 'Getting Started'}
                </Typography>
                <Typography
                    variant={isMobile ? 'h5' : 'h3'}
                    sx={{
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        lineHeight: 1,
                    }}
                >
                    {activeContent.content[currentStep].title}
                </Typography>
            </Stack>

            {/* Image */}
            {activeContent.content[currentStep].image && (
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <img
                        src={activeContent.content[currentStep].image}
                        alt=""
                        style={{
                            width: '100%',

                            objectFit: 'contain',
                        }}
                    />
                    {/* PRELOAD NEXT IMAGE */}
                    {!isLastStep && activeContent.content[currentStep + 1]?.image && (
                        <img src={activeContent.content[currentStep + 1].image} alt="" style={{ display: 'none' }} />
                    )}
                </Box>
            )}

            {/* Description */}
            <Typography variant="body1" textAlign="center" paragraph>
                {activeContent.content[currentStep].description}
            </Typography>
        </BaseDialog>
    )
}
